import React, { useEffect, useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Form, Modal, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import Pagination from 'react-js-pagination';
import * as moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { BaseGatewayImage, HOADON_API, HOADONTHANHTOAN_API, HOSO_TUYENSINH_API } from '@app/utils/apiUrl';
import { DeleteSingle, Get, Post, Put } from '@app/utils/httpProvider';
import { createUrlWithParam } from '../../../../utils/helper';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import { invoiceStatusOptions, invoiceStatusValues, invoiceTypeOptions, invoiceTypeValues, 
    messageToast, objectShowNumber, rangeDateReport, status200 } from '../../../../utils/define';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';
import { normalizeInvoice } from './HoaDonThanhToanForm';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/js/pdf.worker.min.js`;

const valueOf = (item, camel, pascal) => item?.[camel] ?? item?.[pascal];

const getPaymentCode = (item) => {
    const erpId = valueOf(item, 'erpid', 'ERPID');
    return erpId ? String(erpId).split('-')[0] : '';
};

const getErrorMessage = (error, fallback) => {
    const errorData = error?.response?.data ?? error?.data ?? error;

    if (typeof errorData === 'string' && errorData.trim()) return errorData;

    const message = errorData?.message ?? errorData?.Message ?? errorData?.title;
    if (message) return message;

    const cloudInvoiceErrors = Array.isArray(errorData)
        ? errorData.map((item) => item?.errors ?? item?.Errors).filter(Boolean)
        : [];
    if (cloudInvoiceErrors.length > 0) return cloudInvoiceErrors.join('\n');

    if (errorData?.errors && typeof errorData.errors === 'object') {
        const validationErrors = Object.values(errorData.errors).flat().filter(Boolean);
        if (validationErrors.length > 0) return validationErrors.join('\n');
    }

    return fallback;
};

const base64ToPdfUrl = (base64Data) => {
    const normalizedBase64 = base64Data.includes(',')
        ? base64Data.substring(base64Data.indexOf(',') + 1)
        : base64Data;
    const binary = window.atob(normalizedBase64.replace(/\s/g, ''));
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }

    return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
};

const getInvoiceStatusOption = (item) => {
    const value = valueOf(item, 'invoiceStatus', 'InvoiceStatus');
    if (value === null || value === undefined || value === '')
        return { label: 'Chưa xác định', badge: 'light' };
    const numericValue = typeof value === 'string' && invoiceStatusValues[value] !== undefined
        ? invoiceStatusValues[value]
        : Number(value);
    return invoiceStatusOptions[numericValue] || { label: 'Chưa xác định', badge: 'light' };
};

const getInvoiceTypeOption = (item) => {
    const value = valueOf(item, 'invoiceType', 'InvoiceType');
    if (value === null || value === undefined || value === '')
        return { label: 'Chưa xác định', badge: 'light' };
    const numericValue = typeof value === 'string' && invoiceTypeValues[value] !== undefined
        ? invoiceTypeValues[value]
        : Number(value);
    return invoiceTypeOptions[numericValue] || { label: 'Chưa xác định', badge: 'light' };
};

const HoaDonThanhToan = () => {
    const history = useHistory();
    const location = useLocation();
    const valueSearch = useSelector((state) => state.search.valueSearch);
    const params = new URLSearchParams(location.search.substring(1));
    const [page, setPage] = useState(Number(params.get('page')) || 1);
    const [pageSize, setPageSize] = useState(Number(params.get('size')) || 20);
    const [deleteItem, setDeleteItem] = useState(null);
    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
    const [invoicesToIssue, setInvoicesToIssue] = useState([]);
    const [issueType, setIssueType] = useState('issue');
    const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('');
    const [checkedInvoice, setCheckedInvoice] = useState(null);
    const [detailInvoice, setDetailInvoice] = useState(null);
    const [showEvidence, setShowEvidence] = useState(false);
    const [evidencePages, setEvidencePages] = useState(0);
    const [downloadingEvidence, setDownloadingEvidence] = useState(false);
    const [arisingDateRange, setArisingDateRange] = useState({
        startDate: null,
        endDate: null
    });

    const itemAccount = useSelector((state) => state.account.itemAccount);
    const itemAccountAuthInfo = useSelector((state) => state.account?.itemAccountAuthInfo);

    const replaceHistory = (currentPage, size, search) =>
        history.replace(createUrlWithParam(URI_INTERNAL.HOADONTHANHTOAN_PATH, {
            page: currentPage,
            size,
            search
        }));

    const getFilterModel = () => {
        const model = { Keyword: valueSearch || '' };

        if (invoiceStatusFilter !== '') {
            model.InvoiceStatus = Number(invoiceStatusFilter);
        }
        if (arisingDateRange.startDate) {
            model.TuNgay = arisingDateRange.startDate.format('YYYY-MM-DD');
        }
        if (arisingDateRange.endDate) {
            model.DenNgay = arisingDateRange.endDate.format('YYYY-MM-DD');
        }

        return model;
    };

    const { isLoading, isError, error, data, refetch } = useQuery(
        [
            'hoaDonThanhToanData',
            valueSearch,
            page,
            pageSize,
            invoiceStatusFilter,
            arisingDateRange.startDate?.valueOf(),
            arisingDateRange.endDate?.valueOf()
        ],
        () => {
            replaceHistory(page, pageSize, valueSearch);
            return Get(HOADONTHANHTOAN_API.GET_PAGING, null, {
                ...getFilterModel(),
                PageIndex: page,
                PageSize: pageSize
            });
        },
        { keepPreviousData: true }
    );

    const { data: doanhThu = 0, isLoading: isLoadingDoanhThu } = useQuery(
        [
            'hoaDonThanhToanDoanhThu',
            valueSearch,
            invoiceStatusFilter,
            arisingDateRange.startDate?.valueOf(),
            arisingDateRange.endDate?.valueOf()
        ],
        () => Get(HOADONTHANHTOAN_API.GET_DOANH_THU, null, getFilterModel()),
        { keepPreviousData: true }
    );

    const documentLink = (valueOf(detailInvoice, 'documentLink', 'DocumentLink') || '').trim();
    const fileMinhChungUrl = documentLink
        ? (/^https?:\/\//i.test(documentLink)
            ? documentLink
            : `${BaseGatewayImage.replace(/\/$/, '')}/${documentLink.replace(/^\/+/, '')}`)
        : '';

    useEffect(() => {
        setShowEvidence(false);
        setEvidencePages(0);
    }, [detailInvoice]);

    const downloadEvidence = async () => {
        if (!fileMinhChungUrl || downloadingEvidence) return;
        setDownloadingEvidence(true);
        try {
            const response = await fetch(fileMinhChungUrl);
            if (!response.ok) throw new Error('Không thể tải file minh chứng');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = documentLink.split(/[?#]/)[0].split('/').pop() || 'minh-chung.pdf';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error) {
            toast.error('Không thể tải file minh chứng. Vui lòng thử lại.');
        } finally {
            setDownloadingEvidence(false);
        }
    };

    const detailCustomerCode = valueOf(detailInvoice, 'cusCode', 'CusCode') || '';
    const { data: detailApplicantData, isLoading: isLoadingDetailApplicant } = useQuery(
        ['hoaDonThanhToanApplicant', detailCustomerCode],
        () => Get(HOSO_TUYENSINH_API.GET_PAGING, null, {
            Keyword: detailCustomerCode,
            PageIndex: 1,
            PageSize: 20
        }),
        { enabled: !!detailInvoice && !!detailCustomerCode }
    );
    const detailApplicantResult = detailApplicantData?.data?.$values ||
        detailApplicantData?.data || [];
    const detailApplicantItems = Array.isArray(detailApplicantResult)
        ? detailApplicantResult
        : detailApplicantResult?.$values || [];
    const detailApplicant = detailApplicantItems.find((applicant) =>
        String(valueOf(applicant, 'soHoSo', 'SoHoSo') || '') === String(detailCustomerCode)
    ) || detailApplicantItems[0];

    const getDetailValue = (camel, pascal) => {
        if (camel === 'custCode') return getPaymentCode(detailInvoice);

        const applicantFields = {
            cusAddress: ['thuongTruDiaChi', 'ThuongTruDiaChi'],
            cusEmail: ['email', 'Email'],
            soCCCDan: ['soCCCD', 'SoCCCD'],
            cusPhone: ['soDienThoai', 'SoDienThoai']
        };
        const applicantField = applicantFields[camel];
        if (applicantField && detailApplicant) {
            return valueOf(detailApplicant, applicantField[0], applicantField[1]) ?? '';
        }

        return valueOf(detailInvoice, camel, pascal) ?? '';
    };

    useEffect(() => {
        setPage(1);
        replaceHistory(1, pageSize, valueSearch);
    }, [valueSearch]);
    useEffect(() => {
        setSelectedInvoiceIds([]);
        setInvoicesToIssue([]);
    }, [page, pageSize, valueSearch, invoiceStatusFilter, arisingDateRange]);

    const deleteMutation = useMutation((id) =>
        DeleteSingle(HOADONTHANHTOAN_API.DELETE_ITEM, id, null)
    );
    const acceptDelete = () => {
        if (!deleteItem?.id) return;
        deleteMutation.mutate(deleteItem.id, {
            onSuccess: (res) => {
                if (res.status === status200 || res.status === 201) {
                    toast.success(messageToast.messageToastDeleteSuccess);
                    refetch();
                } else toast.error(messageToast.messageToastDeleteError);
            },
            onError: (err) => toast.error(err?.message || messageToast.messageToastTryCatch)
        });
        setDeleteItem(null);
    };

    const issueOptions = {
        issue: {
            api: HOADON_API.PHAT_HANH,
            label: 'Phát hành hóa đơn',
            successLabel: 'hóa đơn',
            requiredStatus: invoiceStatusValues.YeuCauPhatHanh
        },
        replace: {
            api: HOADON_API.THAY_THE,
            label: 'Thay thế',
            successLabel: 'hóa đơn thay thế',
            requiredStatus: invoiceStatusValues.YeuCauThayThe
        },
        decreaseAdjustment: {
            api: HOADON_API.DIEU_CHINH,
            label: 'Điều chỉnh giảm (hoàn tiền)',
            successLabel: 'hóa đơn điều chỉnh giảm',
            requiredStatus: invoiceStatusValues.YeuCauDieuChinhGiam
        }
    };
    const selectedIssueOption = issueOptions[issueType];

    const hasInvalidStatus = (invoices, type) => invoices.some((invoice) => {
        const status = valueOf(invoice, 'invoiceStatus', 'InvoiceStatus');
        const numericStatus = typeof status === 'string' && invoiceStatusValues[status] !== undefined
            ? invoiceStatusValues[status]
            : Number(status);

        return numericStatus !== issueOptions[type].requiredStatus;
    });

    const validateInvoiceStatus = (invoices, type) => {
        if (!hasInvalidStatus(invoices, type)) return true;

        toast.error('Có hóa đơn nhầm trạng thái');
        return false;
    };

    const handleOpenIssueConfirm = () => {
        if (!validateInvoiceStatus(selectedInvoices, issueType)) return;
        setInvoicesToIssue(selectedInvoices);
    };

    const issueMutation = useMutation(({ invoices, type }) => {
        console.log('invoices', invoices);
        const payload = invoices.map((invoice) => {
            const normalizedInvoice = normalizeInvoice(invoice);
            const mdvqhnSachKey = Object.keys(invoice).find(
                (key) => key.toLowerCase() === 'mdvqhnsach'
            );
            normalizedInvoice.mdvqhnSach = mdvqhnSachKey
                ? invoice[mdvqhnSachKey]
                : '';

            // Đây là trạng thái quản lý nội bộ, không phải dữ liệu đầu vào
            // của API phát hành CloudInvoice.
            delete normalizedInvoice.invoiceStatus;
            delete normalizedInvoice.InvoiceStatus;
            delete normalizedInvoice.invoiceType;
            delete normalizedInvoice.InvoiceType;

            return {
                ...normalizedInvoice,
                ...(type === 'decreaseAdjustment' ? { adjType: 2 } : {})
            };
        });
        return Post(issueOptions[type].api, null, payload, false);
    });
    const previewMutation = useMutation(async ({ erpId }) => {
        const response = await Post(HOADON_API.XEM_HOA_DON, null, { ERPID: erpId }, false);
        const responseData = await response.json();
        const results = responseData?.$values ?? responseData;
        const result = Array.isArray(results) ? results[0] : results;
        const previewError = result?.errors ?? result?.Errors;

        if (previewError) throw new Error(previewError);

        const pdfData = result?.data ?? result?.Data;
        if (!pdfData) throw new Error('API không trả về dữ liệu PDF của hóa đơn');

        return base64ToPdfUrl(pdfData);
    });
    const checkStatusMutation = useMutation(async (item) => {
        const erpId = valueOf(item, 'erpid', 'ERPID');
        if (!erpId) throw new Error('Hóa đơn chưa có ERP ID');

        const response = await Post(
            HOADON_API.KIEM_TRA_TRANG_THAI,
            null,
            { ERPID: erpId },
            false
        );
        const responseData = await response.json();
        const results = responseData?.$values ?? responseData;
        const cloudInvoice = Array.isArray(results) ? results[0] : results;

        if (!cloudInvoice) throw new Error('Không nhận được trạng thái hóa đơn');

        const cloudError = valueOf(cloudInvoice, 'errors', 'Errors');
        if (cloudError) throw new Error(cloudError);

        const invoiceStatus = Number(valueOf(cloudInvoice, 'invoiceStatus', 'InvoiceStatus'));
        const invoiceType = Number(valueOf(cloudInvoice, 'invoiceType', 'InvoiceType'));
        if (invoiceStatus !== invoiceStatusValues.PhatHanh ||
            invoiceType !== invoiceTypeValues.ThongThuong) {
            throw new Error('Hóa đơn này chưa phát hành');
        }

        return { item, cloudInvoice };
    });
    const updateStatusMutation = useMutation(({ id, cloudInvoice }) =>
        Put(HOADONTHANHTOAN_API.UPDATE_STATUS_PHAT_HANH, id, cloudInvoice)
    );
    const checkInvoiceStatus = (item) => {
        checkStatusMutation.mutate(item, {
            onSuccess: setCheckedInvoice,
            onError: (err) => toast.error(getErrorMessage(err, 'Không thể kiểm tra trạng thái hóa đơn'))
        });
    };
    const updateCheckedInvoiceStatus = () => {
        const id = itemId(checkedInvoice?.item);
        if (!id) {
            toast.error('Không xác định được hóa đơn cần cập nhật');
            return;
        }

        updateStatusMutation.mutate({ id, cloudInvoice: checkedInvoice.cloudInvoice }, {
            onSuccess: (response) => {
                const responseMessage = response?.data?.message ||
                    'Cập nhật trạng thái hóa đơn thành công';
                if (response?.data?.updated === false) toast.warning(responseMessage);
                else toast.success(responseMessage);
                setCheckedInvoice(null);
                refetch();
            },
            onError: (err) => toast.error(getErrorMessage(err, 'Không thể cập nhật trạng thái hóa đơn'))
        });
    };
    const previewInvoice = (item) => {
        const erpId = valueOf(item, 'erpid', 'ERPID');
        if (!erpId) {
            toast.error('Hóa đơn chưa có ERP ID');
            return;
        }

        const previewWindow = window.open('', '_blank');
        if (!previewWindow) {
            toast.error('Trình duyệt đang chặn cửa sổ xem hóa đơn');
            return;
        }

        previewWindow.document.title = 'Đang tải hóa đơn...';
        previewWindow.document.body.textContent = 'Đang tải hóa đơn...';

        previewMutation.mutate({ erpId }, {
            onSuccess: (pdfUrl) => {
                previewWindow.location.replace(pdfUrl);
                window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
            },
            onError: (err) => {
                previewWindow.close();
                toast.error(getErrorMessage(err, 'Không thể xem hóa đơn'));
            }
        });
    };
    const acceptIssueInvoice = () => {
        if (invoicesToIssue.length === 0) return;
        if (!validateInvoiceStatus(invoicesToIssue, issueType)) return;

        if (issueType !== 'issue') {
            const missingNewErpId = invoicesToIssue.filter(
                (invoice) => !valueOf(invoice, 'newERPID', 'NewERPID')
            );
            if (missingNewErpId.length > 0) {
                toast.error(`${missingNewErpId.length} hóa đơn chưa có ERP ID mới`);
                return;
            }
        }

        console.log('invoicesToIssue', invoicesToIssue);
        const createBy = `${itemAccount?.username || ''}-${itemAccountAuthInfo?.lastName || ''} ${itemAccountAuthInfo?.middleName || ''} ${itemAccountAuthInfo?.firstName || ''}`.trim();
        const invoices = invoicesToIssue.map((invoice) => ({
            ...invoice,
            createBy,
            arisingDate: invoice.arisingDate
                ? moment(invoice.arisingDate).format('DD/MM/YYYY')
                : null
        }));
        const currentIssueType = issueType;
        issueMutation.mutate({ invoices, type: currentIssueType }, {
            onSuccess: () => {
                toast.success(`Phát hành thành công ${invoicesToIssue.length} ${issueOptions[currentIssueType].successLabel}`);
                setInvoicesToIssue([]);
                setSelectedInvoiceIds([]);
                refetch();
            },
            onError: (err) =>
                toast.error(getErrorMessage(err, 'Không thể phát hành hóa đơn'))
        });
    };

    const items = data?.data?.$values || data?.data || [];
    const itemId = (item) => item?.id ?? item?.Id;
    const selectedInvoices = items.filter((item) => selectedInvoiceIds.includes(itemId(item)));
    const isAllSelected = items.length > 0 && selectedInvoices.length === items.length;
    const toggleInvoice = (id) => {
        setSelectedInvoiceIds((ids) =>
            ids.includes(id) ? ids.filter((selectedId) => selectedId !== id) : [...ids, id]
        );
    };
    const toggleAllInvoices = () => {
        setSelectedInvoiceIds(isAllSelected ? [] : items.map(itemId).filter(Boolean));
    };

    const handleApplyArisingDate = (event, picker) => {
        setArisingDateRange({
            startDate: picker.startDate.clone(),
            endDate: picker.endDate.clone()
        });
        setPage(1);
        picker.element.val(
            `${picker.startDate.format('DD/MM/YYYY')} - ${picker.endDate.format('DD/MM/YYYY')}`
        );
    };

    const handleCancelArisingDate = (event, picker) => {
        setArisingDateRange({ startDate: null, endDate: null });
        setPage(1);
        picker.element.val('');
    };

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5>Hóa đơn thanh toán</h5>
                        <div className="d-flex align-items-center">
                            <Form.Control as="select" value={issueType} className="mr-2"
                                style={{ width: 245 }} disabled={issueMutation.isLoading}
                                onChange={(e) => setIssueType(e.target.value)}
                                aria-label="Hình thức xuất hóa đơn">
                                {Object.entries(issueOptions).map(([value, option]) => (
                                    <option key={value} value={value}>{option.label}</option>
                                ))}
                            </Form.Control>
                            <Button variant="success" className="mr-2"
                                disabled={selectedInvoices.length === 0 || issueMutation.isLoading}
                                onClick={handleOpenIssueConfirm}>
                                <i className="fas fa-file-invoice mr-1" />
                                {selectedIssueOption.label} đã chọn ({selectedInvoices.length})
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="row align-items-end mb-3">
                        <div className="col-md-3">
                            <Form.Group className="mb-0">
                                <Form.Label>Trạng thái</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={invoiceStatusFilter}
                                    onChange={(event) => {
                                        setInvoiceStatusFilter(event.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <option value="">Tất cả trạng thái</option>
                                    {Object.entries(invoiceStatusOptions).map(([value, option]) => (
                                        <option key={value} value={value}>{option.label}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </div>
                        <div className="col-md-4">
                            <Form.Group className="mb-0">
                                <Form.Label>Ngày hóa đơn</Form.Label>
                                <DateRangePicker
                                    onApply={handleApplyArisingDate}
                                    onCancel={handleCancelArisingDate}
                                    initialSettings={{
                                        autoUpdateInput: false,
                                        locale: {
                                            format: 'DD/MM/YYYY',
                                            cancelLabel: 'Xóa',
                                            customRangeLabel: 'Tùy chọn',
                                            applyLabel: 'Chọn'
                                        },
                                        linkedCalendars: false,
                                        showDropdowns: true,
                                        ranges: rangeDateReport
                                    }}
                                >
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Chọn khoảng ngày hóa đơn"
                                        readOnly
                                    />
                                </DateRangePicker>
                            </Form.Group>
                        </div>
                        <div className="col-md-5 text-md-right mt-3 mt-md-0">
                            <div className="text-muted">Doanh thu</div>
                            <div className="h4 mb-0 font-weight-bold text-success">
                                {isLoadingDoanhThu
                                    ? 'Đang tính...'
                                    : `${Number(doanhThu || 0).toLocaleString('vi-VN')} VNĐ`}
                            </div>
                        </div>
                    </div>
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th className="text-center">
                                        <Form.Check type="checkbox" checked={isAllSelected}
                                            onChange={toggleAllInvoices}
                                            aria-label="Chọn tất cả hóa đơn trong trang" />
                                    </th>
                                    <th>STT</th><th>ERP ID</th><th>New ERP ID</th><th>Số hóa đơn</th><th>Ký hiệu</th><th>Mẫu số</th>
                                    <th>Khách hàng</th><th>Mã số thuế</th><th>Ngày hóa đơn</th><th>Trạng thái</th><th>Loại</th>
                                    <th className="text-nowrap text-right">Thành tiền</th>
                                    <th className="text-nowrap">Hành động</th>
                                </tr>
                            </thead>
                            <tbody>
                               {isLoading ? <tr><td colSpan={13}>Đang tải...</td></tr>
                                    : isError ? <tr><td colSpan={13}>Lỗi: {error?.message}</td></tr>
                                    : items.length === 0 ? <tr><td colSpan={13}>Không có dữ liệu</td></tr>
                                    : items.map((item, index) => {
                                        const statusOption = getInvoiceStatusOption(item);
                                        const typeOption = getInvoiceTypeOption(item);
                                        return (
                                        <tr key={item.id || index}>
                                            <td className="text-center">
                                                <Form.Check type="checkbox"
                                                    checked={selectedInvoiceIds.includes(itemId(item))}
                                                    onChange={() => toggleInvoice(itemId(item))}
                                                    aria-label={`Chọn hóa đơn ${valueOf(item, 'erpid', 'ERPID') || ''}`} />
                                            </td>
                                            <td>{(page - 1) * pageSize + index + 1}</td>
                                            <td>{valueOf(item, 'erpid', 'ERPID')}</td>
                                            <td>{valueOf(item, 'newERPID', 'NewERPID')}</td>
                                            <td>{valueOf(item, 'invoiceNo', 'InvoiceNo')}</td>
                                            <td>{valueOf(item, 'serial', 'Serial')}</td>
                                            <td>{valueOf(item, 'pattern', 'Pattern')}</td>
                                            <td>{valueOf(item, 'cusName', 'CusName') || valueOf(item, 'cusCompanyName', 'CusCompanyName')}</td>
                                            <td>{valueOf(item, 'cusTaxCode', 'CusTaxCode')}</td>
                                            <td>
                                                {valueOf(item, 'arisingDate', 'ArisingDate')
                                                    ? moment(valueOf(item, 'arisingDate', 'ArisingDate')).format('DD/MM/YYYY')
                                                    : ''}
                                            </td>
                                            <td>
                                                <span className={`badge badge-${statusOption.badge}`}>
                                                    {statusOption.label}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge badge-${typeOption.badge}`}>
                                                    {typeOption.label}
                                                </span>
                                            </td>
                                            <td className="text-nowrap text-right">{Number(valueOf(item, 'amount', 'Amount') || 0).toLocaleString('vi-VN')}</td>
                                            <td>
                                                <div className="d-flex flex-column align-items-start" style={{ minWidth: 115, gap: 8 }}>
                                                <Button variant="primary" size="sm" className="mr-2"
                                                    title="Xem chi tiết thông tin hóa đơn"
                                                    onClick={() => setDetailInvoice(item)}>
                                                    <i className="fas fa-eye mr-1" />
                                                    Chi tiết
                                                </Button>
                                                {/* <Button variant="success" size="sm" className="mr-2"
                                                    disabled={issueMutation.isLoading}
                                                    onClick={() => setInvoicesToIssue([item])}>
                                                    <i className="fas fa-file-invoice mr-1" />
                                                    {selectedIssueOption.label}
                                                </Button> */}
                                                {/* <NavLink className="btn btn-primary btn-sm mr-2"
                                                    to={`${URI_INTERNAL.HOADONTHANHTOAN_EDIT_PATH}?id=${item.id}`}>Sửa</NavLink> */}
                                                <Button variant="info" size="sm" className="mr-2"
                                                    disabled={previewMutation.isLoading}
                                                    onClick={() => previewInvoice(item)}>
                                                    <i className="fas fa-print mr-1" />
                                                    In hóa đơn
                                                </Button>
                                                <Button variant="warning" size="sm" className="mr-2"
                                                    disabled={checkStatusMutation.isLoading}
                                                    onClick={() => checkInvoiceStatus(item)}>
                                                    <i className="fas fa-sync-alt mr-1" />
                                                    Kiểm tra
                                                </Button>
                                                {/* <Button variant="danger" size="sm"
                                                    onClick={() => setDeleteItem(item)}>Xóa</Button> */}
                                                </div>
                                            </td>
                                        </tr>
                                        );
                                    })}
                            </tbody>
                        </Table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                        <Form.Control as="select" value={pageSize} style={{ width: 100 }}
                            onChange={(e) => setPageSize(Number(e.target.value))}>
                            {objectShowNumber.map((opt) => <option key={opt.value} value={opt.value}>{opt.text}</option>)}
                        </Form.Control>
                        <Pagination itemClass="page-item" linkClass="page-link"
                            innerClass="pagination pagination-sm no-margin" activePage={page}
                            itemsCountPerPage={pageSize} totalItemsCount={data?.totalRecords || 0}
                            pageRangeDisplayed={5} onChange={setPage} />
                    </div>
                    <ConfirmModal isOpen={!!deleteItem} confirmType="delete"
                        handleAccept={acceptDelete} handleCancel={() => setDeleteItem(null)}
                        content={valueOf(deleteItem, 'erpid', 'ERPID')} />
                    <ConfirmModal isOpen={invoicesToIssue.length > 0}
                        handleAccept={acceptIssueInvoice}
                        handleCancel={() => {
                            if (!issueMutation.isLoading) setInvoicesToIssue([]);
                        }}
                        content={invoicesToIssue.length === 1
                            ? `hóa đơn ERP ID ${valueOf(invoicesToIssue[0], 'erpid', 'ERPID') || ''}`
                            : `${invoicesToIssue.length} hóa đơn đã chọn`}
                        labelAccept={selectedIssueOption.label} />
                    <Modal show={!!detailInvoice && !showEvidence} onHide={() => setDetailInvoice(null)}
                        size="lg" centered>
                        <Modal.Header closeButton className="bg-primary text-white">
                            <Modal.Title>
                                <i className="fas fa-file-invoice mr-2" />
                                Chi tiết thông tin hóa đơn
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {isLoadingDetailApplicant && (
                                <div className="text-center text-muted mb-3">
                                    <i className="fas fa-spinner fa-spin mr-2" />
                                    Đang tải thông tin hồ sơ...
                                </div>
                            )}
                            <div className="row">
                                {[
                                    ['Mã thanh toán', 'custCode', 'CustCode'],
                                    ['Mã khách hàng', 'cusCode', 'CusCode'],
                                    ['Mã số thuế', 'cusTaxCode', 'CusTaxCode'],
                                    ['Tên đơn vị', 'cusCompanyName', 'CusCompanyName'],
                                    ['Địa chỉ', 'cusAddress', 'CusAddress'],
                                    ['Email', 'cusEmail', 'CusEmail'],
                                    ['Số CCCD', 'soCCCDan', 'SoCCCDan'],
                                    ['Mã ĐVQHNS', 'mdvqhnSach', 'MDVQHNSach'],
                                    ['Số tài khoản', 'cusBankNo', 'CusBankNo'],
                                    ['Ngân hàng', 'cusBankName', 'CusBankName'],
                                    ['Điện thoại', 'cusPhone', 'CusPhone'],
                                    ['Fax', 'cusFax', 'CusFax']
                                ].map(([label, camel, pascal]) => (
                                    <div className="col-md-6" key={camel}>
                                        <Form.Group>
                                            <Form.Label>{label}</Form.Label>
                                            <Form.Control
                                                value={getDetailValue(camel, pascal)}
                                                readOnly
                                            />
                                        </Form.Group>
                                    </div>
                                ))}
                                <div className="col-md-12">
                                    <Form.Group className="mb-0">
                                        <Form.Label>Ghi chú</Form.Label>
                                        <Form.Control as="textarea" rows={3}
                                            value={valueOf(detailInvoice, 'note', 'Note') ?? ''}
                                            readOnly />
                                    </Form.Group>
                                </div>
                            </div>
                            {!fileMinhChungUrl && (
                                <div className="text-muted mt-3">Chưa có file minh chứng.</div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="outline-primary" disabled={!fileMinhChungUrl}
                                onClick={() => setShowEvidence(true)}>
                                <i className="fas fa-eye mr-2" />Xem file minh chứng
                            </Button>
                            <Button variant="primary" disabled={!fileMinhChungUrl || downloadingEvidence}
                                onClick={downloadEvidence}>
                                <i className={`fas ${downloadingEvidence ? 'fa-spinner fa-spin' : 'fa-download'} mr-2`} />
                                Tải file minh chứng
                            </Button>
                            <Button variant="outline-secondary"
                                onClick={() => setDetailInvoice(null)}>
                                Đóng
                            </Button>
                        </Modal.Footer>
                    </Modal>
                    <Modal show={!!detailInvoice && showEvidence} onHide={() => setShowEvidence(false)}
                        size="lg" centered>
                        <Modal.Header closeButton className="bg-primary text-white">
                            <Modal.Title>File minh chứng</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ maxHeight: '75vh', overflow: 'auto' }}>
                            {showEvidence && fileMinhChungUrl && (
                                <Document file={fileMinhChungUrl}
                                    onLoadSuccess={({ numPages }) => setEvidencePages(numPages)}
                                    loading="Đang tải file minh chứng..."
                                    error="Không thể xem file minh chứng. Vui lòng tải file để xem.">
                                    {Array.from({ length: evidencePages }, (_, index) => (
                                        <Page key={index + 1} pageNumber={index + 1}
                                            width={Math.min(720, window.innerWidth - 64)}
                                            renderAnnotationLayer={false} renderTextLayer={false} />
                                    ))}
                                </Document>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="outline-secondary" onClick={() => setShowEvidence(false)}>
                                Đóng
                            </Button>
                        </Modal.Footer>
                    </Modal>
                    <Modal show={!!checkedInvoice} onHide={() => setCheckedInvoice(null)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Trạng thái hóa đơn Cloud Invoice</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Table bordered size="sm" className="mb-0">
                                <tbody>
                                    {[
                                        ['ERP ID', 'erpid', 'ERPID'],
                                        ['Mẫu số', 'pattern', 'Pattern'],
                                        ['Ký hiệu', 'serial', 'Serial'],
                                        ['Số hóa đơn', 'invoiceNo', 'InvoiceNo'],
                                        ['Trạng thái', 'invoiceStatus', 'InvoiceStatus'],
                                        ['Loại hóa đơn', 'invoiceType', 'InvoiceType'],
                                        ['Lỗi', 'errors', 'Errors']
                                    ].map(([label, camel, pascal]) => (
                                        <tr key={camel}>
                                            <th style={{ width: '35%' }}>{label}</th>
                                            <td>{valueOf(checkedInvoice?.cloudInvoice, camel, pascal) || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setCheckedInvoice(null)}
                                disabled={updateStatusMutation.isLoading}>
                                Đóng
                            </Button>
                            <Button variant="success" onClick={updateCheckedInvoiceStatus}
                                disabled={updateStatusMutation.isLoading}>
                                {updateStatusMutation.isLoading &&
                                    <i className="fas fa-spinner fa-spin mr-1" />}
                                Cập nhật trạng thái
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default HoaDonThanhToan;
